import { FC, useEffect, useState } from 'react';
import { Box, withStyles, CircleButton, CportalServiceItem, CportalService } from 'vopay';
import { useTranslation } from 'react-i18next';
import { Title, Toggle } from 'UI';
import CredentialsDialog from './CredentialsDialog';
import Services from './Services';
import useCportal from '@/hooks/useApi/useCportal';
import styles from './CportalTab.module.scss';
import { useAlertDialog, useBlockingLoader, useDialog, useToast } from 'shared';

const cn = withStyles(styles);

type CportalTabProps = {
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  clientID: string;
  isAccountActive: boolean;
  cPortalLink: string;
  clientAccountServices: CportalServiceItem[];
  accountServices: CportalServiceItem[];
  isFetchingServices: boolean;
  loadServices: () => void;
  onUpdate: (enabled?: boolean) => void;
  onToggleService: (service: CportalService, enabled: boolean) => Promise<void>;
};

const CportalTab: FC<CportalTabProps> = ({
  clientName,
  clientEmail,
  clientPhone,
  clientID,
  isAccountActive,
  cPortalLink,
  clientAccountServices,
  accountServices,
  isFetchingServices,
  loadServices,
  onUpdate,
  onToggleService,
}) => {
  const dialog = useDialog();
  const alertDialog = useAlertDialog();
  const toast = useToast();
  const { active, sendCportalEmail, fetchUserAccount, deactivateCportalUser } = useCportal();
  const { t } = useTranslation('translations', { keyPrefix: 'Customers.CportalTab' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const [shouldEnableCportal, setShouldEnableCportal] = useState(false);
  const blockingLoader = useBlockingLoader();

  useEffect(() => {
    // A disabled client account can't be resolved by the cPortal endpoints
    // (they reject inactive accounts with "ClientAccountID is not valid"), so
    // skip the fetch entirely rather than surface a misleading error.
    if (!isAccountActive) return;

    blockingLoader.show();

    fetchUserAccount({
      ClientAccountID: clientID,
    })
      .then((res) => {
        setShouldEnableCportal(res?.User.IsActive);
      })
      .catch((err) => {
        alertDialog.error(tGlobal('error'), err instanceof Error ? err.message : tGlobal('errorOccurred'));
      })
      .finally(() => {
        blockingLoader.hide();
      });
  }, [clientID, isAccountActive]);

  useEffect(() => {
    if (!isAccountActive) return;
    loadServices();
  }, [clientID, isAccountActive]);

  async function handleCredentialsDialog() {
    setShouldEnableCportal(true);
    let wasEnabled = false;

    await dialog.open(
      ({ onCancel }) => (
        <CredentialsDialog
          clientName={clientName}
          clientEmail={clientEmail}
          clientPhone={clientPhone}
          clientID={clientID}
          onUpdate={() => {
            wasEnabled = true;
            onUpdate(true);
          }}
          onClose={onCancel}
        />
      ),
      { bare: true },
    );

    if (wasEnabled) await fetchUserAccount({ ClientAccountID: clientID });
    else setShouldEnableCportal(false);
  }

  async function handleDisableDialog() {
    const result = await alertDialog.caution(
      t('disableCportalTitle', { defaultValue: 'Disable cPortal' }),
      t('disableCportalDescription', {
        defaultValue:
          'Are you sure you would like to disable cPortal access for this client account? The client will no longer be able to log in to cPortal under your account.',
      }),
      {
        abortLabel: tGlobal('Buttons.back', { defaultValue: 'Back' }),
        confirmLabel: tGlobal('Buttons.confirm', { defaultValue: 'Confirm' }),
      },
    );

    if (result === 'confirm') {
      try {
        blockingLoader.show();
        await deactivateCportalUser({ ClientAccountID: clientID });
        setShouldEnableCportal(false);

        toast.success(
          t('disableCportalSuccess', {
            defaultValue: 'cPortal Access Disabled Successfully',
          }),
        );

        await fetchUserAccount({ ClientAccountID: clientID });
        onUpdate();
      } catch (err) {
        await alertDialog.caution(
          t('disableCportalErrorTitle', { defaultValue: 'Unable to Disable cPortal' }),
          err instanceof Error ? err.message : tGlobal('errorOccurred'),
          {
            confirmLabel: tGlobal('Buttons.okay', { defaultValue: 'Okay' }),
          },
        );
      } finally {
        blockingLoader.hide();
      }
    }
  }

  async function handleResendDialog() {
    const result = await alertDialog.caution(
      t('resendInviteTitle', { defaultValue: 'Resend Invite' }),
      t('resendInviteDescription', {
        defaultValue: 'Are you sure you would like to resend invitation to this client account?',
      }),
      {
        abortLabel: tGlobal('Buttons.back', { defaultValue: 'Back' }),
        confirmLabel: tGlobal('Buttons.confirm', { defaultValue: 'Confirm' }),
      },
    );

    if (result === 'confirm') {
      try {
        blockingLoader.show();

        await sendCportalEmail({
          ClientAccountID: clientID,
          EmailType: 'WelcomeEmail',
        });

        toast.success(
          t('sendCportalEmailSuccess', {
            defaultValue: 'cPortal Invitation Sent Successfully',
          }),
        );
      } catch {
        toast.neutral(
          t('sendCportalEmailError', {
            defaultValue: 'Failed to send the cPortal welcome email',
          }),
        );
      } finally {
        blockingLoader.hide();
      }
    }
  }

  return (
    <>
      <div className={cn('titleContainer')}>
        <Toggle
          name="enable-cportal-check"
          checked={shouldEnableCportal}
          readOnly={!isAccountActive}
          onChange={async (e) => {
            if (!isAccountActive) return;

            if (e.target.checked) {
              await handleCredentialsDialog();
            } else {
              await handleDisableDialog();
            }
          }}
          label={<Title level="5">{t('title', { defaultValue: 'cPortal' })}</Title>}
        />
      </div>

      {active && (
        <div className={cn('cportalLink')}>
          <span>{t('cportalLink', { defaultValue: 'cPortal Link' })}</span>

          <Box gapSize="12px" ai="center">
            <span className={cn('link')}>{cPortalLink}</span>

            <CircleButton
              icon="content_copy"
              circleFill={false}
              colorScheme="action"
              onClick={() => {
                navigator.clipboard.writeText(cPortalLink.toString());
              }}
            />

            <CircleButton icon="send" circleFill={false} colorScheme="action" iconFill onClick={handleResendDialog} />
          </Box>
        </div>
      )}

      <Services
        isCportalEnabled={shouldEnableCportal}
        clientAccountServices={clientAccountServices}
        accountServices={accountServices}
        isFetchingServices={isFetchingServices}
        onToggleService={onToggleService}
      />
    </>
  );
};

export default CportalTab;
